import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  makeStyles,
  CircularProgress,
  Card,
  CardContent,
  Box,
  Grid,
  MenuItem,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import RefreshIcon from "@material-ui/icons/Refresh";
import DateFnsUtils from "@date-io/date-fns";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";

import axios from "src/utils/axiosHub";
import axiosTechnician from "src/utils/axios";
import { getUser } from "src/utils/Common";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    minHeight: "calc(100vh - 100px)",
    padding: theme.spacing(3),
    background: "linear-gradient(135deg, #eef2f7, #f7f9fc)",
  },
  card: {
    width: "100%",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    borderRadius: "20px",
    padding: theme.spacing(3),
    backgroundColor: "#fff",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
    },
  },
  header: {
    marginBottom: theme.spacing(3),
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#2c3e50",
    borderBottom: "3px solid #00c6ff",
    paddingBottom: theme.spacing(1),
  },
  field: {
    marginBottom: theme.spacing(2.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fafafa",
      transition: "all 0.3s ease",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: "#00c6ff",
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: "#0072ff",
      boxShadow: "0 0 0 3px rgba(0,114,255,0.2)",
    },
  },
  buttonWrapper: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(3),
  },
  button: {
    minWidth: 200,
    fontWeight: 600,
    padding: theme.spacing(1.3, 4),
    borderRadius: "30px",
    background: "linear-gradient(90deg, #0072ff, #00c6ff)",
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: "1px",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(90deg, #005fcc, #00aaff)",
      transform: "translateY(-2px) scale(1.02)",
      boxShadow: "0 8px 20px rgba(0,114,255,0.3)",
    },
    "&:disabled": {
      background: "#bdc3c7",
      color: "#f5f5f5",
      transform: "none",
      boxShadow: "none",
    },
  },
  progress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  alertBox: {
    borderRadius: "12px",
    fontSize: "0.95rem",
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    "& .MuiAlert-icon": {
      fontSize: "1.4rem",
    },
  },
  autoOption: {
    "&[data-focus='true']": {
      backgroundColor: "rgba(0, 114, 255, 0.08)",
    },
    "&[aria-selected='true']": {
      backgroundColor: "rgba(0, 114, 255, 0.15)",
      fontWeight: 600,
    },
  },
  checkBox: {
    marginTop: "-8px",
    marginBottom: "12px",
    paddingLeft: "4px",
    "& .MuiFormControlLabel-label": {
      fontWeight: 500,
      color: "#2c3e50",
      fontSize: "0.9rem",
    },
    "& .MuiCheckbox-root": {
      padding: "4px 8px",
      color: "#0072ff",
      "&:hover": {
        backgroundColor: "rgba(0,114,255,0.08)",
        borderRadius: "6px",
      },
    },
  },
  resetIconButton: {
    minWidth: "40px",
    borderRadius: "50%",
    border: "2px solid #2196f3",
    color: "#2196f3",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(33, 150, 243, 0.1)",
      borderColor: "#1976d2",
      color: "#1976d2",
    },
  },
}));

export default function UpliftRequestForm() {
  const classes = useStyles();
  const SessionUser = getUser();

  const [urunData, setUrunData] = useState(null);
  const [options, setOptions] = useState([]);
  const [openAuto, setOpenAuto] = useState(false);
  const [loadingAuto, setLoadingAuto] = useState(false);

  const [exchangeReasons, setExchangeReasons] = useState([]);
  const [appType, setAppType] = useState({ dsc: "Exchange", id: null });
  const [warrantyInfo, setWarrantyInfo] = useState({
    dsc: "In Warranty",
    id: 0,
  });
  const [warrantyTypes, setWarrantyTypes] = useState([]);

  const [formData, setFormData] = useState({
    externalClaimNr: "",
    dateOfPurchase: null,
    serialNumber: "",
    comment: "",
    cantFindModel: false,
    freeTextModel: "",
    exchangeReasonId: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const REQUIRED_MSG = "This field is required!";

  useEffect(() => {
    axiosTechnician
      .get("/GetSiriusWarrantyType")
      .then((res) => setWarrantyTypes(res.data?.length ? res.data : []))
      .catch(() => setWarrantyTypes([]));
  }, []);

  useEffect(() => {
    axiosTechnician
      .get("/GetSiriusIssueByProduct/Exchange/0")
      .then((res) => {
        const item = (res.data || []).find((i) => i.ExternalId === "305");
        if (item) {
          setAppType({
            dsc: item.Dsc,
            id: item.Id,
          });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!warrantyTypes.length) return;

    const match = warrantyTypes.find((w) => w.LocalId === 7);

    if (match) {
      setWarrantyInfo({
        dsc: match.FieldText,
        id: match.WarrantyId,
      });
    }
  }, [warrantyTypes]);

  useEffect(() => {
    axios
      .get("/api/Technician/GetChangeReason")
      .then((res) => {
        setExchangeReasons(
          res.data?.length ? res.data.filter((i) => i.GroupId === 11) : []
        );
      })
      .catch(() => setExchangeReasons([]));
  }, []);

  const getProductId = (product) => {
        return product?.Id || 0;
    };

  const getProductLabel = (option) => {
    if (!option) return "";
    return option.Dsc || "";
  };

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleCantFindModelChange = (e) => {
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      cantFindModel: checked,
      freeTextModel: checked ? prev.freeTextModel : "",
    }));

    if (checked) {
      setUrunData(null);
      setOptions([]);
      setOpenAuto(false);
    }

    setErrors((prev) => ({
      ...prev,
      modelCode: undefined,
      freeTextModel: undefined,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.externalClaimNr || !formData.externalClaimNr.trim()) {
      newErrors.externalClaimNr = REQUIRED_MSG;
    }

    if (!formData.serialNumber || !formData.serialNumber.trim()) {
      newErrors.serialNumber = REQUIRED_MSG;
    }

    if (!formData.comment || !formData.comment.trim()) {
      newErrors.comment = REQUIRED_MSG;
    }

    if (!formData.exchangeReasonId) {
      newErrors.exchangeReasonId = REQUIRED_MSG;
    }

    if (formData.cantFindModel) {
      if (!formData.freeTextModel || !formData.freeTextModel.trim()) {
        newErrors.freeTextModel = REQUIRED_MSG;
      }
    } else {
      const selectedProductId = getProductId(urunData);

      if (!urunData || !selectedProductId) {
        newErrors.modelCode = REQUIRED_MSG;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const reset = () => {
    setFormData({
      externalClaimNr: "",
      dateOfPurchase: null,
      serialNumber: "",
      comment: "",
      cantFindModel: false,
      freeTextModel: "",
      exchangeReasonId: "",
    });

    setUrunData(null);
    setOptions([]);
    setOpenAuto(false);
    setErrors({});
    setSnackbar({
      open: false,
      type: "success",
      text: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!appType.id || !warrantyInfo.id) {
      setSnackbar({
        open: true,
        type: "error",
        text: "Application type or warranty type could not be loaded.",
      });
      return;
    }

    setLoading(true);

    const selectedProductId = getProductId(urunData);

    const payload = {
      ExternalClaimNr: formData.externalClaimNr || null,
      ProductId: formData.cantFindModel ? null : selectedProductId,
      SerialNr: formData.serialNumber,
      IssueId: appType.id,
      WarrantyTypeId: warrantyInfo.id,
      Comment: formData.comment,
      FreetxtModel: formData.cantFindModel ? formData.freeTextModel : null,
      DateOfPurchase: formData.dateOfPurchase || null,
      CreateBy: SessionUser?.TechnicianId,
      ExchangeReasonId: Number(formData.exchangeReasonId),
    };

    try {
      const res = await axios.post(
        "/api/TradePartner/setUKCurryCompensationData",
        payload
      );

      const result = res.data;

      if (result?.Success) {
        reset();

        setSnackbar({
            open: true,
            type: "success",
            text: "Registration successful! DocumentNr: " + (result.Id || ""),
        });
        } else {
        setSnackbar({
            open: true,
            type: "error",
            text:
            "Registration failed! " +
            (result?.ErrorReason || result?.ErrorMessage || "Unknown error"),
        });
        }
    } catch (err) {
      setSnackbar({
        open: true,
        type: "error",
        text:
          "API Error: " +
          (err.response?.data?.ErrorMessage ||
            err.response?.data?.Message ||
            err.message ||
            "Unknown error"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (value) => {
    if (!value || value.length <= 3) {
      setOptions([]);
      return;
    }

    setLoadingAuto(true);

    try {
      const res = await axiosTechnician.get(
        `/GetProductWorkCaseSearch/${value.toLocaleLowerCase(
          localStorage.i18nextLng_normalized
        )}`
      );

      setOptions(res.data || []);
    } catch {
      setOptions([]);
    } finally {
      setLoadingAuto(false);
    }
  };

  return (
    <div className={classes.pageWrapper}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" className={classes.header}>
            Uplift Request Form
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              label="External Claim Nr"
              variant="outlined"
              fullWidth
              className={classes.field}
              value={formData.externalClaimNr}
              onChange={handleChange("externalClaimNr")}
              error={!!errors.externalClaimNr}
              helperText={errors.externalClaimNr}
            />

            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                label="Date of Purchase"
                inputVariant="outlined"
                format="dd/MM/yyyy"
                fullWidth
                disableFuture
                className={classes.field}
                value={formData.dateOfPurchase}
                onChange={(date) =>
                  setFormData((prev) => ({
                    ...prev,
                    dateOfPurchase: date,
                  }))
                }
                KeyboardButtonProps={{ "aria-label": "change date" }}
                error={!!errors.dateOfPurchase}
                helperText={errors.dateOfPurchase}
              />
            </MuiPickersUtilsProvider>

            <TextField
              label="Application Type"
              variant="outlined"
              fullWidth
              className={classes.field}
              value={appType.dsc}
              disabled
            />

            <TextField
              label="Warranty Type"
              variant="outlined"
              fullWidth
              className={classes.field}
              value={warrantyInfo.dsc}
              disabled
            />

            <TextField
              label="Return Reason"
              variant="outlined"
              fullWidth
              select
              className={classes.field}
              value={formData.exchangeReasonId}
              onChange={handleChange("exchangeReasonId")}
              error={!!errors.exchangeReasonId}
              helperText={errors.exchangeReasonId}
            >
              {exchangeReasons.map((r) => (
                <MenuItem key={r.Id} value={r.Id}>
                  {r.MainLanguageDescription}
                </MenuItem>
              ))}
            </TextField>

            {formData.cantFindModel ? (
              <TextField
                label="Free Text Model"
                variant="outlined"
                fullWidth
                className={classes.field}
                value={formData.freeTextModel}
                onChange={handleChange("freeTextModel")}
                error={!!errors.freeTextModel}
                helperText={errors.freeTextModel}
              />
            ) : (
              <Autocomplete
                open={openAuto}
                onOpen={() => setOpenAuto(true)}
                onClose={() => setOpenAuto(false)}
                options={options}
                value={urunData}
                classes={{ option: classes.autoOption }}
                getOptionSelected={(option, value) =>
                  getProductId(option) === getProductId(value)
                }
                onChange={(event, newValue) => {
                  setUrunData(newValue);

                  setErrors((prev) => ({
                    ...prev,
                    modelCode: undefined,
                  }));
                }}
                onInputChange={(event, value, reason) => {
                  if (reason === "input") {
                    handleSearch(value);
                  }
                }}
                getOptionLabel={getProductLabel}
                filterOptions={(opts) => opts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    variant="outlined"
                    fullWidth
                    className={classes.field}
                    error={!!errors.modelCode}
                    helperText={errors.modelCode}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAuto ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <FormControlLabel
              className={classes.checkBox}
              control={
                <Checkbox
                  checked={formData.cantFindModel}
                  onChange={handleCantFindModelChange}
                  color="primary"
                />
              }
              label="Can't find the model"
            />

            <TextField
              label="Serial Number"
              variant="outlined"
              fullWidth
              className={classes.field}
              value={formData.serialNumber}
              onChange={handleChange("serialNumber")}
              error={!!errors.serialNumber}
              helperText={errors.serialNumber}
            />

            <TextField
              label="Comment"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              className={classes.field}
              value={formData.comment}
              onChange={handleChange("comment")}
              error={!!errors.comment}
              helperText={errors.comment}
            />

            {snackbar.open && (
              <Box mb={2}>
                <Alert
                  className={classes.alertBox}
                  severity={snackbar.type}
                  onClose={() =>
                    setSnackbar((prev) => ({
                      ...prev,
                      open: false,
                    }))
                  }
                >
                  {snackbar.text}
                </Alert>
              </Box>
            )}

            <Grid container alignItems="center" className={classes.buttonWrapper}>
              <Grid item xs={12} sm="auto" md>
                <Box
                  display="flex"
                  justifyContent={{ xs: "center", md: "center" }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    disabled={loading || !appType.id || !warrantyInfo.id}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm="auto" md="auto">
                <Box
                  display="flex"
                  justifyContent={{ xs: "center", md: "flex-end" }}
                  mt={{ xs: 1, md: 0 }}
                >
                  <Button className={classes.resetIconButton} onClick={reset}>
                    <RefreshIcon />
                  </Button>
                </Box>
              </Grid>

              {loading && (
                <CircularProgress size={24} className={classes.progress} />
              )}
            </Grid>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}